import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { obtenerBodegas, resumenConsolidado } from '../../utils/api';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// ─────────────────────────────────────────
// CUENTAS POR BODEGA
// ─────────────────────────────────────────
const CUENTAS = {
  'Nuestro':       { efectivo: 'Bancolombia Nuestro',      banco: 'Davivienda Nuestro' },
  'Buenavista':    { efectivo: 'Bancolombia Buenavista',   banco: 'Davivienda Buenavista' },
  'Parque Alegra': { efectivo: 'Bancolombia Empresarial',  banco: 'Bancolombia Empresarial' },
  'Alamedas':      { efectivo: 'Bancolombia Empresarial',  banco: 'Bancolombia Empresarial' },
};

const getCuentas = (nombre) => CUENTAS[nombre] || { efectivo: '—', banco: '—' };

// ─────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────
const COP = (n) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
    .format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;

// ─────────────────────────────────────────
// CAMPO MONEDA
// ─────────────────────────────────────────
function CampoMoneda({ label, valor, onChange, disabled, hint, icon }) {
  const [raw, setRaw] = useState(String(valor || ''));
  const [focused, setFocused] = useState(false);

  useEffect(() => { if (!focused) setRaw(String(valor || '')); }, [valor, focused]);

  const handleChange = (e) => {
    const v = e.target.value.replace(/[^\d]/g, '');
    setRaw(v);
    onChange(parseFloat(v) || 0);
  };

  return (
    <div style={cm.wrapper}>
      <label style={cm.label}>
        {icon && <span>{icon} </span>}
        {label}
        {hint && <span style={cm.hint}> {hint}</span>}
      </label>
      <div style={{ ...cm.inputWrap, borderColor: focused ? '#1a1a2e' : '#E5E7EB' }}>
        <span style={cm.prefix}>$</span>
        <input
          type="text" inputMode="numeric"
          value={focused ? raw : (valor > 0 ? COP(valor).replace(/[$\s]/g, '') : '')}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          style={cm.input} placeholder="0"
        />
      </div>
    </div>
  );
}

const cm = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  hint: { fontWeight: 400, color: '#9CA3AF', textTransform: 'none' },
  inputWrap: { display: 'flex', alignItems: 'center', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', overflow: 'hidden', transition: 'border-color 0.15s' },
  prefix: { padding: '0 10px', color: '#9CA3AF', fontSize: 15, fontWeight: 700, background: '#F9FAFB', alignSelf: 'stretch', display: 'flex', alignItems: 'center', borderRight: '1px solid #E5E7EB' },
  input: { flex: 1, padding: '10px 12px', border: 'none', outline: 'none', fontSize: 15, fontWeight: 600, color: '#111827', background: 'transparent', fontFamily: "'Courier New', monospace" },
};

// ─────────────────────────────────────────
// BLOQUE DE SISTEMA (Aliaddo o Emergencia)
// ─────────────────────────────────────────
function BloqueSistema({ titulo, icon, color, prefix, form, onChange, disabled, conRappi }) {
  const p = prefix; // 'aliaddo' o 'emergencia'

  const totalEfectivo   = num(form[`${p}_efectivo`]);
  const totalTarjeta    = num(form[`${p}_tarjeta`]);
  const totalTransf     = num(form[`${p}_transferencia`]);
  const totalRappi      = conRappi ? num(form[`${p}_rappi`]) : 0;
  const totalBonos      = num(form[`${p}_bonos`]);
  const totalSistema    = totalEfectivo + totalTarjeta + totalTransf + totalRappi + totalBonos;

  return (
    <div style={{ ...bs.card, borderTop: `3px solid ${color}` }}>
      <div style={bs.header}>
        <div style={bs.titleRow}>
          <span style={{ ...bs.dot, background: color }} />
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={bs.title}>{titulo}</span>
        </div>
        <div style={{ ...bs.total, color }}>
          Total: <strong>{COP(totalSistema)}</strong>
        </div>
      </div>
      <div style={bs.body}>
        <CampoMoneda label="Efectivo" icon="💵" valor={form[`${p}_efectivo`]} onChange={onChange(`${p}_efectivo`)} disabled={disabled} />
        <CampoMoneda label="Tarjeta débito/crédito" icon="💳" valor={form[`${p}_tarjeta`]} onChange={onChange(`${p}_tarjeta`)} disabled={disabled} />
        <CampoMoneda label="Transferencia / QR / Nequi" icon="📱" valor={form[`${p}_transferencia`]} onChange={onChange(`${p}_transferencia`)} disabled={disabled} />
        {conRappi && (
          <CampoMoneda label="Rappi" icon="🛵" hint="(liquidación propia)" valor={form[`${p}_rappi`]} onChange={onChange(`${p}_rappi`)} disabled={disabled} />
        )}
        <CampoMoneda label="Bonos" icon="🎟️" hint="(no van al banco)" valor={form[`${p}_bonos`]} onChange={onChange(`${p}_bonos`)} disabled={disabled} />
      </div>

      {/* Subtotales del sistema */}
      <div style={bs.subtotales}>
        <div style={bs.subtotal}>
          <span>💵 Efectivo</span>
          <strong style={{ color: '#10B981' }}>{COP(totalEfectivo)}</strong>
        </div>
        <div style={bs.subtotal}>
          <span>🏦 Banco (tarjeta + transf)</span>
          <strong style={{ color: '#3B82F6' }}>{COP(totalTarjeta + totalTransf)}</strong>
        </div>
        {conRappi && (
          <div style={bs.subtotal}>
            <span>🛵 Rappi</span>
            <strong style={{ color: '#F97316' }}>{COP(totalRappi)}</strong>
          </div>
        )}
        <div style={bs.subtotal}>
          <span>🎟️ Bonos</span>
          <strong style={{ color: '#8B5CF6' }}>{COP(totalBonos)}</strong>
        </div>
      </div>
    </div>
  );
}

const bs = {
  card: { background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden', marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px 12px', borderBottom: '1px solid #F3F4F6' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 8 },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  title: { fontSize: 14, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.06em' },
  total: { fontSize: 14, fontWeight: 600 },
  body: { padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14 },
  subtotales: { padding: '10px 20px 14px', background: '#F9FAFB', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 16, flexWrap: 'wrap' },
  subtotal: { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: '#6B7280' },
};

// ─────────────────────────────────────────
// RESUMEN CONSOLIDADO DEL DÍA
// ─────────────────────────────────────────
function ResumenDia({ form, bodegaNombre, totalReal, onRealChange, diferencia, disabled, ventasEfectivo }) {
  const cuentas = getCuentas(bodegaNombre);

  const totalEfectivo   = ventasEfectivo || 0; // desde ventas reales
  const totalBanco      = num(form.aliaddo_tarjeta) + num(form.aliaddo_transferencia) + num(form.emergencia_tarjeta) + num(form.emergencia_transferencia);
  const totalRappi      = num(form.aliaddo_rappi);
  const totalBonos      = num(form.aliaddo_bonos) + num(form.emergencia_bonos);
  const totalEntradas   = num(form.entradas_efectivo);
  const totalSalidas    = num(form.compras) + num(form.otras_salidas);
  const totalEsperado   = totalEfectivo + totalEntradas - totalSalidas;
  const dif             = totalReal - totalEsperado;
  const cuadra          = Math.abs(dif) < 100;

  return (
    <div style={{ background: 'white', borderRadius: 14, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden', border: '2px solid #F3F4F6' }}>
      <div style={{ padding: '14px 20px', background: '#1a1a2e', color: 'white' }}>
        <div style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>📊 Resumen del Día</div>
      </div>

      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>

        <div style={rd.item}>
          <div style={rd.itemIcon}>💵</div>
          <div style={rd.itemLabel}>Efectivo en ventas</div>
          <div style={{ ...rd.itemValor, color: '#10B981' }}>{COP(totalEfectivo)}</div>
        </div>
        <div style={rd.item}>
          <div style={rd.itemIcon}>💰</div>
          <div style={rd.itemLabel}>Entradas de efectivo</div>
          <div style={{ ...rd.itemValor, color: '#10B981' }}>{COP(totalEntradas)}</div>
        </div>
        <div style={rd.item}>
          <div style={rd.itemIcon}>📤</div>
          <div style={rd.itemLabel}>Salidas</div>
          <div style={{ ...rd.itemValor, color: '#EF4444' }}>{COP(totalSalidas)}</div>
        </div>
      </div>

      {/* Cuadre de efectivo */}
      <div style={{ padding: '14px 20px', background: '#F9FAFB', borderTop: '1px solid #F3F4F6' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
          Cuadre de Efectivo
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 4 }}>Entradas - Salidas</div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: 'monospace' }}>{COP(totalEsperado)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, marginBottom: 4 }}>Efectivo real contado</div>
            <div style={{ ...cm.inputWrap, borderColor: '#1a1a2e', maxWidth: 200 }}>
              <span style={cm.prefix}>$</span>
              <input
                type="text" inputMode="numeric"
                value={totalReal || ''}
                onChange={e => onRealChange(parseFloat(e.target.value.replace(/[^\d]/g, '')) || 0)}
                disabled={disabled}
                style={{ ...cm.input, fontSize: 18 }}
                placeholder="0"
              />
            </div>
          </div>
          <div style={{
            padding: '12px 16px', borderRadius: 10,
            background: cuadra ? '#D1FAE5' : dif < 0 ? '#FEE2E2' : '#FEF3C7',
            color: cuadra ? '#065F46' : dif < 0 ? '#991B1B' : '#92400E',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
              {cuadra ? '✅ CUADRA' : dif < 0 ? '❌ FALTANTE' : '⚠️ SOBRANTE'}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'monospace' }}>
              {dif >= 0 ? '+' : ''}{COP(dif)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const rd = {
  item: { background: '#F9FAFB', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 3 },
  itemIcon: { fontSize: 20, marginBottom: 2 },
  itemLabel: { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' },
  itemValor: { fontSize: 20, fontWeight: 800, fontFamily: 'monospace' },
  itemCuenta: { fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' },
};

// ─────────────────────────────────────────
// MODAL CONFIRMACIÓN
// ─────────────────────────────────────────
function ModalConfirmacion({ form, bodegaNombre, totalReal, diferencia, onConfirmar, onCancelar }) {
  const cuentas = getCuentas(bodegaNombre);
  const totalEfectivo = num(form.aliaddo_efectivo) + num(form.emergencia_efectivo);
  const totalBanco    = num(form.aliaddo_tarjeta) + num(form.aliaddo_transferencia) + num(form.emergencia_tarjeta) + num(form.emergencia_transferencia);
  const totalRappi    = num(form.aliaddo_rappi);
  const cuadra        = Math.abs(diferencia) < 100;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', background: '#1a1a2e', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>¿Confirmas el cierre del día?</h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.7 }}>Una vez enviado quedará bloqueado</p>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>💵 EFECTIVO → {cuentas.efectivo}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#10B981', fontFamily: 'monospace' }}>{COP(totalEfectivo)}</div>
            </div>
            <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>🏦 BANCO → {cuentas.banco}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#3B82F6', fontFamily: 'monospace' }}>{COP(totalBanco)}</div>
            </div>
            {totalRappi > 0 && (
              <div style={{ background: '#FFF7ED', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>🛵 RAPPI (liquidación propia)</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#F97316', fontFamily: 'monospace' }}>{COP(totalRappi)}</div>
              </div>
            )}
            <div style={{
              background: cuadra ? '#F0FDF4' : diferencia < 0 ? '#FEF2F2' : '#FFFBEB',
              borderRadius: 10, padding: '10px 14px',
            }}>
              <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700 }}>
                {cuadra ? '✅ CUADRA' : diferencia < 0 ? '❌ FALTANTE' : '⚠️ SOBRANTE'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'monospace', color: cuadra ? '#10B981' : diferencia < 0 ? '#EF4444' : '#F59E0B' }}>
                {diferencia >= 0 ? '+' : ''}{COP(diferencia)}
              </div>
            </div>
          </div>

          {!cuadra && (
            <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#92400E' }}>
              ⚠️ Hay una diferencia en el efectivo. ¿Estás seguro de que los datos son correctos?
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid #F3F4F6', justifyContent: 'flex-end' }}>
          <button onClick={onCancelar} style={{ padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Revisar de nuevo
          </button>
          <button onClick={onConfirmar} style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#1D4ED8,#1E40AF)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            🔒 Confirmar y Bloquear
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// MODAL SOBRE
// ─────────────────────────────────────────
function ModalSobre({ cierreId, bodegaId, bodegaNombre, fecha, onClose, onSaved }) {
  const cuentas = getCuentas(bodegaNombre);
  const [monto, setMonto] = useState(0);
  const [tipo, setTipo] = useState('consignacion_efectivo');
  const [numero, setNumero] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  const tiposSobre = [
    { val: 'consignacion_efectivo', label: `💵 Consig. efectivo → ${cuentas.efectivo}` },
    { val: 'consignacion_banco',    label: `🏦 Tarjetas/Transf → ${cuentas.banco}` },
    { val: 'entrega_gerencia',      label: '🤝 Entrega a Gerencia' },
  ];

  const handleSave = async () => {
    if (!monto || monto <= 0) { toast.error('Ingresa el monto'); return; }
    if (!numero) { toast.error('Ingresa el número de comprobante'); return; }
    setGuardando(true);
    try {
      await API.post('/caja/sobre', { cierre_caja_id: cierreId, bodega_id: bodegaId, fecha, monto, tipo_comprobante: tipo, numero_comprobante: numero, notas });
      toast.success('Sobre registrado');
      onSaved();
      onClose();
    } catch { toast.error('Error al guardar sobre'); }
    finally { setGuardando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #F3F4F6' }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>💵 Registrar Sobre</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>Tipo</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tiposSobre.map(t => (
                <button key={t.val} onClick={() => setTipo(t.val)} style={{ padding: '10px 14px', border: `2px solid ${tipo === t.val ? '#1a1a2e' : '#E5E7EB'}`, borderRadius: 10, background: tipo === t.val ? '#1a1a2e' : 'white', color: tipo === t.val ? 'white' : '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'left' }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <CampoMoneda label="Monto" valor={monto} onChange={setMonto} />
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
              {tipo === 'entrega_gerencia' ? 'Nombre de quien recibe' : 'Número de comprobante'}
            </label>
            <input value={numero} onChange={e => setNumero(e.target.value)} style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} placeholder={tipo === 'entrega_gerencia' ? 'Ej: Juan García' : 'Ej: 0123456789'} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>Notas (opcional)</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} rows={2} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid #F3F4F6', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Cancelar</button>
          <button onClick={handleSave} disabled={guardando} style={{ padding: '10px 24px', border: 'none', borderRadius: 8, background: 'linear-gradient(135deg,#059669,#047857)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            {guardando ? 'Guardando...' : '✓ Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// VISTA CONSOLIDADA
// ─────────────────────────────────────────
function VistaConsolidada({ fecha }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    resumenConsolidado(fecha)
      .then(r => setData(r.data))
      .catch(() => toast.error('Error cargando resumen'))
      .finally(() => setCargando(false));
  }, [fecha]);

  if (cargando) return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando...</div>;
  if (!data) return null;

  return (
    <div>
      {data.puntos?.map((p, i) => {
        const cuentas = getCuentas(p.bodega);
        return (
          <div key={i} style={{ background: 'white', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12 }}>🏪 {p.bodega}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, fontSize: 13 }}>
              <div style={{ background: '#F0FDF4', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ color: '#6B7280', fontSize: 11 }}>💵 Efectivo → {cuentas.efectivo}</div>
                <div style={{ fontWeight: 800, color: '#10B981', fontFamily: 'monospace' }}>{COP(num(p.total_efectivo))}</div>
              </div>
              <div style={{ background: '#EFF6FF', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ color: '#6B7280', fontSize: 11 }}>🏦 Banco → {cuentas.banco}</div>
                <div style={{ fontWeight: 800, color: '#3B82F6', fontFamily: 'monospace' }}>{COP(num(p.total_banco))}</div>
              </div>
              <div style={{ background: '#FFF7ED', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ color: '#6B7280', fontSize: 11 }}>🛵 Rappi</div>
                <div style={{ fontWeight: 800, color: '#F97316', fontFamily: 'monospace' }}>{COP(num(p.total_rappi))}</div>
              </div>
              <div style={{ background: '#F5F3FF', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ color: '#6B7280', fontSize: 11 }}>
                  {Math.abs(num(p.diferencia)) < 100 ? '✅ Cuadra' : num(p.diferencia) < 0 ? '❌ Faltante' : '⚠️ Sobrante'}
                </div>
                <div style={{ fontWeight: 800, fontFamily: 'monospace', color: Math.abs(num(p.diferencia)) < 100 ? '#10B981' : num(p.diferencia) < 0 ? '#EF4444' : '#F59E0B' }}>
                  {num(p.diferencia) >= 0 ? '+' : ''}{COP(p.diferencia)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────
// ESTADO INICIAL DEL FORM
// ─────────────────────────────────────────
const FORM_INICIAL = {
  entradas_efectivo: 0,
  aliaddo_efectivo: 0,
  aliaddo_tarjeta: 0,
  aliaddo_transferencia: 0,
  aliaddo_rappi: 0,
  aliaddo_bonos: 0,
  emergencia_efectivo: 0,
  emergencia_tarjeta: 0,
  emergencia_transferencia: 0,
  emergencia_bonos: 0,
  compras: 0,
  otras_salidas: 0,
  descripcion_otras_salidas: '',
  observaciones: '',
};

const estadoEstilo = (e) => ({
  aprobado:    { background: '#D1FAE5', color: '#065F46' },
  enviado:     { background: '#DBEAFE', color: '#1E40AF' },
  con_novedad: { background: '#FEE2E2', color: '#991B1B' },
  borrador:    { background: '#F3F4F6', color: '#6B7280' },
  revisado:    { background: '#FEF3C7', color: '#92400E' },
}[e] || { background: '#F3F4F6', color: '#6B7280' });

// ─────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────
export default function ControlCaja() {
  const { usuario, puedeVer } = useAuth();
  const esAdmin = puedeVer('gerencia', 'administracion');

  const [bodegas, setBodegas]         = useState([]);
  const [bodegaId, setBodegaId]       = useState(null);
  const [fecha, setFecha]             = useState(format(new Date(), 'yyyy-MM-dd'));
  const [vistaConsolidada, setVistaConsolidada] = useState(false);
  const [cierreId, setCierreId]       = useState(null);
  const [estado, setEstado]           = useState('borrador');
  const [bloqueado, setBloqueado]     = useState(false);
  const [form, setForm]               = useState(FORM_INICIAL);
  const [totalReal, setTotalReal]     = useState(0);
  const [sobres, setSobres]           = useState([]);
  const [ventasEfectivo, setVentasEfectivo] = useState(0);
  const [guardando, setGuardando]     = useState(false);
  const [modalSobre, setModalSobre]   = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalCorreccion, setModalCorreccion] = useState(false);
  const [cargando, setCargando]       = useState(true);
  const [verHistorial, setVerHistorial] = useState(false);
  const [historial, setHistorial]     = useState([]);

  const soloLectura = bloqueado || (estado === 'aprobado' && !esAdmin);

  // Cargar bodegas
  useEffect(() => {
    obtenerBodegas().then(r => {
      const pvs = r.data.filter(b => b.tipo === 'punto_venta');
      setBodegas(pvs);
      if (puedeVer('punto_venta')) {
        const mia = pvs.find(b => b.area_id === usuario.area_id);
        if (mia) setBodegaId(mia.id);
      } else if (pvs.length > 0) {
        setBodegaId(pvs[0].id);
      }
    });
  }, []);

  // Cargar cierre
  useEffect(() => {
    if (!bodegaId) return;
    setCargando(true);
    API.get(`/caja/cierre/${bodegaId}`, { params: { fecha } })
      .then(({ data }) => {
        if (data.existe) {
          const c = data.cierre;
          setCierreId(c.id);
          setEstado(c.estado);
          setBloqueado(c.bloqueado || false);
          setTotalReal(num(c.total_real));
          setSobres(data.sobres || []);
          setVentasEfectivo(num(c.total_efectivo));
          setForm({

            aliaddo_efectivo:         num(c.aliaddo_efectivo),
            aliaddo_tarjeta:          num(c.aliaddo_tarjeta),
            aliaddo_transferencia:    num(c.aliaddo_transferencia),
            aliaddo_rappi:            num(c.aliaddo_rappi),
            aliaddo_bonos:            num(c.aliaddo_bonos),
            emergencia_efectivo:      num(c.emergencia_efectivo),
            emergencia_tarjeta:       num(c.emergencia_tarjeta),
            emergencia_transferencia: num(c.emergencia_transferencia),
            emergencia_bonos:         num(c.emergencia_bonos),
            compras:                  num(c.compras),
            otras_salidas:            num(c.otras_salidas),
            descripcion_otras_salidas: c.descripcion_otras_salidas || '',
            observaciones:            c.observaciones || '',
          });
        } else {
          setCierreId(null);
          setEstado('borrador');
          setBloqueado(false);
          setTotalReal(0);
          setSobres([]);
          setForm(FORM_INICIAL);
          setVentasEfectivo(num(data.ventas_efectivo));
        }
      })
      .catch(() => toast.error('Error cargando cierre'))
      .finally(() => setCargando(false));
  }, [bodegaId, fecha]);

  const set = (campo) => (val) => setForm(f => ({ ...f, [campo]: val }));
  const setStr = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }));

  // Totales
  const totalEfectivo   = ventasEfectivo; // calculado desde ventas reales en BD
  const totalBanco      = num(form.aliaddo_tarjeta) + num(form.aliaddo_transferencia) + num(form.emergencia_tarjeta) + num(form.emergencia_transferencia);
  const totalEntradas   = num(form.entradas_efectivo);
  const totalSalidas    = num(form.compras) + num(form.otras_salidas);
  const totalEsperado   = totalEfectivo + totalEntradas - totalSalidas;
  const diferencia      = totalReal - totalEsperado;

  const handleGuardar = async (nuevoEstado) => {
    if (!bodegaId) return;
    setGuardando(true);
    try {
      const payload = { ...form, bodega_id: bodegaId, fecha, total_real: totalReal, diferencia, estado: nuevoEstado || estado };
      const { data } = await API.post('/caja/cierre', payload);
      if (!cierreId) setCierreId(data.cierre.id);
      setEstado(data.cierre.estado);
      toast.success('Cierre guardado');
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const handleBloquearYEnviar = async () => {
    setGuardando(true);
    try {
      const payload = { ...form, bodega_id: bodegaId, fecha, total_real: totalReal, diferencia, estado: 'enviado' };
      const { data } = await API.post('/caja/cierre', payload);
      const id = data.cierre.id || cierreId;
      await API.post(`/caja/${id}/bloquear`);
      setCierreId(id);
      setEstado('enviado');
      setBloqueado(true);
      toast.success('¡Cierre enviado y bloqueado!');
    } catch { toast.error('Error al enviar cierre'); }
    finally { setGuardando(false); setModalConfirm(false); }
  };

  const cargarHistorial = async () => {
    if (!bodegaId) return;
    API.get(`/caja/historial/${bodegaId}`).then(r => setHistorial(r.data)).catch(() => {});
  };

  const bodegaActual = bodegas.find(b => b.id === bodegaId);
  const fechaFormateada = format(new Date(fecha + 'T12:00:00'), "EEEE d 'de' MMMM", { locale: es });

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>💰 Control de Caja</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14, textTransform: 'capitalize' }}>{fechaFormateada}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {esAdmin && (
            <button onClick={() => setVistaConsolidada(!vistaConsolidada)} style={{ padding: '9px 16px', border: '2px solid #E5E7EB', borderRadius: 10, background: vistaConsolidada ? '#1a1a2e' : 'white', color: vistaConsolidada ? 'white' : '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {vistaConsolidada ? '📋 Ver Cierre' : '📊 Ver Consolidado'}
            </button>
          )}
          <span style={{ padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, ...estadoEstilo(estado) }}>
            {{ borrador: '📝 Borrador', enviado: '📤 Enviado', revisado: '🔍 Revisado', aprobado: '✅ Aprobado', con_novedad: '⚠️ Con Novedad' }[estado]}
          </span>
          {bloqueado && <span style={{ fontSize: 18 }}>🔒</span>}
        </div>
      </div>

      {/* Controles */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Fecha</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} max={format(new Date(), 'yyyy-MM-dd')} style={{ padding: '9px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', fontWeight: 600 }} />
        </div>
        {esAdmin && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Punto de Venta</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {bodegas.map(b => (
                <button key={b.id} onClick={() => setBodegaId(b.id)} style={{ padding: '8px 14px', border: '2px solid', borderColor: bodegaId === b.id ? '#1a1a2e' : '#E5E7EB', borderRadius: 20, background: bodegaId === b.id ? '#1a1a2e' : 'white', color: bodegaId === b.id ? 'white' : '#6B7280', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  🏪 {b.nombre}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {vistaConsolidada ? <VistaConsolidada fecha={fecha} /> : cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando cierre...</div>
      ) : (
        <>


          {/* Nota informativa — ventas calculadas automáticamente */}
          <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 12, padding: '12px 18px', fontSize: 13, color: '#0369A1', fontWeight: 600, marginBottom: 16 }}>
            ℹ️ Los totales de ventas son calculados automáticamente por el sistema en el módulo Cuadre de Ingresos
          </div>

          {/* Salidas */}
          <div style={{ background: 'white', borderRadius: 14, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden', borderTop: '3px solid #EF4444' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 20px 12px', borderBottom: '1px solid #F3F4F6' }}>
              <span style={{ fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>📤 Salidas de Efectivo</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#EF4444' }}>Total: <strong>{COP(totalSalidas)}</strong></span>
            </div>
            <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              <CampoMoneda label="Entradas de efectivo" icon="💰" valor={form.entradas_efectivo} onChange={set('entradas_efectivo')} disabled={soloLectura} hint="(menudo, adelantos recibidos)" />
              <CampoMoneda label="Compras del día" icon="🛒" valor={form.compras} onChange={set('compras')} disabled={soloLectura} />
              <div>
                <CampoMoneda label="Otras salidas" icon="➖" valor={form.otras_salidas} onChange={set('otras_salidas')} disabled={soloLectura} />
                {num(form.otras_salidas) > 0 && (
                  <input placeholder="Descripción" value={form.descripcion_otras_salidas} onChange={setStr('descripcion_otras_salidas')} disabled={soloLectura} style={{ marginTop: 6, width: '100%', padding: '8px 12px', border: '2px solid #FDE68A', borderRadius: 8, fontSize: 13, outline: 'none', background: '#FFFBEB', boxSizing: 'border-box' }} />
                )}
              </div>
            </div>
          </div>

          {/* Resumen */}
          <ResumenDia form={form} bodegaNombre={bodegaActual?.nombre} totalReal={totalReal} onRealChange={setTotalReal} diferencia={diferencia} disabled={soloLectura} ventasEfectivo={ventasEfectivo} />

          {/* Observaciones */}
          <div style={{ background: 'white', borderRadius: 14, padding: '16px 20px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 8 }}>📝 Observaciones</label>
            <textarea value={form.observaciones} onChange={setStr('observaciones')} disabled={soloLectura} placeholder="Novedades del día, explicación de diferencias..." rows={3} style={{ padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' }} />
          </div>

          {/* Acciones */}
          {!soloLectura && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <button onClick={() => handleGuardar('borrador')} disabled={guardando} style={{ padding: '12px 24px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                {guardando ? '⏳ Guardando...' : '💾 Guardar Borrador'}
              </button>
              {(estado === 'borrador' || estado === 'con_novedad') && (
                <button onClick={() => setModalConfirm(true)} disabled={guardando} style={{ padding: '12px 24px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#1D4ED8,#1E40AF)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                  📤 Enviar y Cerrar Cierre
                </button>
              )}
              {esAdmin && estado === 'enviado' && (
                <>
                  <button onClick={() => handleGuardar('aprobado')} style={{ padding: '12px 24px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#059669,#047857)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                    ✅ Aprobar
                  </button>
                  <button onClick={() => handleGuardar('con_novedad')} style={{ padding: '12px 24px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#D97706,#B45309)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                    ⚠️ Marcar Novedad
                  </button>
                </>
              )}
            </div>
          )}

          {bloqueado && !esAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#92400E', fontWeight: 600 }}>
              <span>🔒 Este cierre está bloqueado y enviado a revisión.</span>
              <button onClick={() => setModalCorreccion(true)} style={{ padding: '8px 14px', border: '1px solid #F59E0B', borderRadius: 8, background: 'white', color: '#92400E', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                ✏️ Solicitar Corrección
              </button>
            </div>
          )}

          {/* Historial */}
          {esAdmin && (
            <div style={{ marginBottom: 32 }}>
              <button onClick={() => { setVerHistorial(!verHistorial); if (!verHistorial) cargarHistorial(); }} style={{ padding: '9px 18px', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                {verHistorial ? '▲ Ocultar Historial' : '📅 Ver Historial'}
              </button>
              {verHistorial && (
                <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  {historial.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF' }}>Sin historial</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#1a1a2e', color: 'white' }}>
                          {['Fecha', 'Efectivo', 'Banco', 'Rappi', 'Diferencia', 'Estado', ''].map(h => (
                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {historial.map((h, i) => {
                          const dif = num(h.diferencia);
                          return (
                            <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                              <td style={{ padding: '10px 14px', fontWeight: 600 }}>{format(new Date(h.fecha + 'T12:00'), 'd MMM yyyy', { locale: es })}</td>
                              <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#10B981', fontWeight: 700 }}>{COP(h.total_efectivo)}</td>
                              <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#3B82F6', fontWeight: 700 }}>{COP(h.total_banco)}</td>
                              <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: '#F97316', fontWeight: 700 }}>{COP(h.total_rappi)}</td>
                              <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 700, color: Math.abs(dif) < 100 ? '#10B981' : dif < 0 ? '#EF4444' : '#F59E0B' }}>
                                {dif >= 0 ? '+' : ''}{COP(dif)}
                              </td>
                              <td style={{ padding: '10px 14px' }}>
                                <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, ...estadoEstilo(h.estado) }}>{h.estado}</span>
                              </td>
                              <td style={{ padding: '10px 14px' }}>{h.bloqueado ? '🔒' : '🔓'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {modalCorreccion && (
        <ModalSolicitudCorreccion
          cierreId={cierreId}
          bodegaId={bodegaId}
          onClose={() => setModalCorreccion(false)}
        />
      )}

      {modalConfirm && (
        <ModalConfirmacion
          form={form} bodegaNombre={bodegaActual?.nombre}
          totalReal={totalReal} diferencia={diferencia}
          onConfirmar={handleBloquearYEnviar}
          onCancelar={() => setModalConfirm(false)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// MODAL SOLICITUD DE CORRECCIÓN
// ─────────────────────────────────────────
function ModalSolicitudCorreccion({ cierreId, bodegaId, onClose }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!motivo.trim()) { toast.error('Describe el motivo de corrección'); return; }
    setEnviando(true);
    try {
      await API.post('/correcciones/solicitar', {
        tipo: 'cierre_caja',
        registro_id: cierreId,
        bodega_id: bodegaId,
        motivo,
      });
      toast.success('Solicitud enviada a administración');
      onClose();
    } catch { toast.error('Error al enviar solicitud'); }
    finally { setEnviando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>✏️ Solicitar Corrección</h3>
        <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>
          Explica qué necesitas corregir en este cierre. Administración recibirá tu solicitud y podrá desbloquear el registro.
        </p>
        <textarea
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          placeholder="Describe el error y qué debe corregirse..."
          rows={4}
          style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16 }}
        />
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Cancelar
          </button>
          <button onClick={handleEnviar} disabled={enviando || !motivo.trim()} style={{ padding: '10px 20px', border: 'none', borderRadius: 8, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            {enviando ? 'Enviando...' : '📤 Enviar solicitud'}
          </button>
        </div>
      </div>
    </div>
  );
}
